import { useState, useMemo } from 'react'
import { X, ChevronRight, ChevronLeft } from 'lucide-react'
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isBefore, isAfter, isWithinInterval } from 'date-fns'
import { ar } from 'date-fns/locale'

function CalendarGrid({ displayMonth, selectedStart, selectedEnd, onDateClick, rangeHover, onHoverDate }) {
  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(displayMonth), { weekStartsOn: 6 })
    const end = endOfWeek(endOfMonth(displayMonth), { weekStartsOn: 6 })
    return eachDayOfInterval({ start, end })
  }, [displayMonth])

  const headerDays = ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س']

  function getDayStyle(day) {
    const isSelectedStart = selectedStart && isSameDay(day, selectedStart)
    const isSelectedEnd = selectedEnd && isSameDay(day, selectedEnd)
    const inRange = selectedStart && rangeHover && !selectedEnd && isAfter(day, selectedStart) && isBefore(day, rangeHover)
    const inRangeSelected = selectedStart && selectedEnd && isWithinInterval(day, { start: selectedStart, end: selectedEnd })

    const base = {
      width: 36, height: 36, borderRadius: 8,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, cursor: 'pointer', border: 'none',
      background: 'transparent',
      fontFamily: 'Cairo, sans-serif', fontWeight: 600,
      transition: 'all 0.1s',
    }

    if (!isSameMonth(day, displayMonth)) {
      return { ...base, color: '#E0E0E0', cursor: 'default' }
    }

    if (isSelectedStart || isSelectedEnd) {
      return {
        ...base, background: '#000666', color: '#fff',
        borderRadius: isSelectedStart && isSelectedEnd ? 8 :
          isSelectedStart && selectedEnd ? '8px 0 0 8px' :
          selectedStart && isSelectedEnd ? '0 8px 8px 0' : 8,
      }
    }

    if (inRange || inRangeSelected) {
      return { ...base, background: 'rgba(0,6,102,0.08)', color: '#000666', borderRadius: 0 }
    }

    const isToday = isSameDay(day, new Date())
    return {
      ...base, color: '#1A1C1C',
      background: isToday ? 'rgba(0,6,102,0.06)' : 'transparent',
    }
  }

  return (
    <div style={{ userSelect: 'none' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 6 }}>
        {headerDays.map((d) => (
          <div key={d} style={{ textAlign: 'center', fontSize: 11, color: '#767683', fontWeight: 700, fontFamily: 'Cairo, sans-serif', padding: '4px 0' }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {days.map((day) => (
          <button
            key={day.toISOString()}
            onClick={() => onDateClick(day)}
            onMouseEnter={() => onHoverDate?.(day)}
            style={getDayStyle(day)}
            disabled={!isSameMonth(day, displayMonth)}
          >
            {format(day, 'd')}
          </button>
        ))}
      </div>
    </div>
  )
}

export default function DateRangePicker({ open, onClose, onApply, initialStart, initialEnd }) {
  const today = new Date()
  const [startDate, setStartDate] = useState(initialStart || null)
  const [endDate, setEndDate] = useState(initialEnd || null)
  const [step, setStep] = useState('start')
  const [month1, setMonth1] = useState(today)
  const [month2, setMonth2] = useState(addMonths(today, 1))

  const [rangeHover, setRangeHover] = useState(null)

  const displayStart = startDate ? format(startDate, 'yyyy/MM/dd') : '—'
  const displayEnd = endDate ? format(endDate, 'yyyy/MM/dd') : '—'

  function handleDateClick(day) {
    if (!isSameMonth(day, month1) && !isSameMonth(day, month2)) return

    if (step === 'start') {
      setStartDate(day)
      setEndDate(null)
      setRangeHover(null)
      setStep('end')
    } else {
      if (isBefore(day, startDate)) {
        setStartDate(day)
        setEndDate(null)
        setRangeHover(null)
        return
      }
      setEndDate(day)
      setRangeHover(null)
      setStep('done')
    }
  }

  function handleApply() {
    if (startDate && endDate) {
      onApply({
        from: format(startDate, 'yyyy-MM-dd'),
        to: format(endDate, 'yyyy-MM-dd'),
      })
    }
    onClose()
  }

  function handleClear() {
    setStartDate(null)
    setEndDate(null)
    setStep('start')
  }

  function goBack() {
    setMonth1(subMonths(month1, 1))
    setMonth2(subMonths(month2, 1))
  }

  function goForward() {
    setMonth1(addMonths(month1, 1))
    setMonth2(addMonths(month2, 1))
  }

  if (!open) return null

  const canApply = startDate && endDate

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 24,
          width: '100%', maxWidth: 640,
          boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #F0F0F0',
        }}>
          <div>
            <div style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 900, fontSize: 16, color: '#1A1C1C' }}>
              تحديد نطاق التاريخ
            </div>
            <div style={{ fontFamily: 'Tajawal, sans-serif', fontSize: 12, color: '#767683', marginTop: 2, direction: 'ltr' }}>
              {step === 'start' ? 'اختر تاريخ البداية' : step === 'end' ? 'اختر تاريخ النهاية' : ''}
            </div>
          </div>
          <button onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 10,
              background: '#F0F0F5', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#767683', flexShrink: 0,
            }}>
            <X size={16} />
          </button>
        </div>

        {/* Selected range display */}
        <div style={{
          display: 'flex', gap: 12, padding: '12px 20px',
          background: '#F9F9F9', borderBottom: '1px solid #F0F0F0',
          fontFamily: 'Tajawal, sans-serif', fontSize: 13,
        }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#767683' }}>من:</span>
            <span style={{ fontWeight: 700, color: '#000666', direction: 'ltr' }}>{displayStart}</span>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#767683' }}>إلى:</span>
            <span style={{ fontWeight: 700, color: '#000666', direction: 'ltr' }}>{displayEnd}</span>
          </div>
        </div>

        {/* Calendars */}
        <div style={{ padding: '16px 20px', display: 'flex', gap: 24, justifyContent: 'center' }}>
          {/* Month 1 */}
          <div style={{ flex: 1, maxWidth: 260 }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginBottom: 12,
            }}>
              <button onClick={goBack}
                style={{
                  width: 28, height: 28, borderRadius: 7, border: 'none',
                  background: 'rgba(0,6,102,0.06)', color: '#000666',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <ChevronRight size={14} />
              </button>
              <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 14, color: '#1A1C1C' }}>
                {format(month1, 'MMMM yyyy', { locale: ar })}
              </span>
            </div>
            <CalendarGrid
              displayMonth={month1}
              selectedStart={startDate}
              selectedEnd={endDate}
              onDateClick={handleDateClick}
              rangeHover={rangeHover}
              onHoverDate={setRangeHover}
            />
          </div>

          {/* Month 2 */}
          <div style={{ flex: 1, maxWidth: 260 }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginBottom: 12,
            }}>
              <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 800, fontSize: 14, color: '#1A1C1C' }}>
                {format(month2, 'MMMM yyyy', { locale: ar })}
              </span>
              <button onClick={goForward}
                style={{
                  width: 28, height: 28, borderRadius: 7, border: 'none',
                  background: 'rgba(0,6,102,0.06)', color: '#000666',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <ChevronLeft size={14} />
              </button>
            </div>
            <CalendarGrid
              displayMonth={month2}
              selectedStart={startDate}
              selectedEnd={endDate}
              onDateClick={handleDateClick}
              rangeHover={rangeHover}
              onHoverDate={setRangeHover}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderTop: '1px solid #F0F0F0',
        }}>
          <button onClick={handleClear}
            style={{
              padding: '8px 16px', borderRadius: 10, border: 'none',
              background: '#F0F0F5', color: '#767683', cursor: 'pointer',
              fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13,
            }}>
            إعادة تعيين
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose}
              style={{
                padding: '8px 20px', borderRadius: 10, border: 'none',
                background: '#F3F3F3', color: '#454652', cursor: 'pointer',
                fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13,
              }}>
              إلغاء
            </button>
            <button onClick={handleApply} disabled={!canApply}
              style={{
                padding: '8px 20px', borderRadius: 10, border: 'none',
                background: canApply ? '#000666' : '#C4C4C4', color: '#fff', cursor: canApply ? 'pointer' : 'default',
                fontFamily: 'Cairo, sans-serif', fontWeight: 700, fontSize: 13,
              }}>
              تطبيق
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
